import { Link } from 'react-router-dom'
import {
  Building2,
  Stethoscope,
  Heart,
  Truck,
  Laptop,
  Hotel,
  GraduationCap,
  Landmark,
  Briefcase,
  ArrowRight,
} from 'lucide-react'
import LoopCTAs from '../../components/LoopCTAs'
import Seo from '../../components/Seo'
import { FadeIn } from '../../components/Motion'

const industries = [
  {
    icon: Building2,
    name: 'Financial Services',
    description: 'Customer experience, member feedback, compliance research.',
    href: '/industries/financial-services',
    color: '#3b82f6',
    subPages: ['Banks & Credit Unions', 'Retirement Planning', 'Insurance'],
  },
  {
    icon: Stethoscope,
    name: 'Healthcare',
    description: 'Patient experience, staff engagement, quality improvement.',
    href: '/industries/healthcare',
    color: '#8b5cf6',
    subPages: ['Hospitals', 'Clinics & Medical Groups', 'Dental Practices'],
  },
  {
    icon: Heart,
    name: 'Nonprofit',
    description: 'Donor feedback, member engagement, program evaluation.',
    href: '/industries/nonprofit',
    color: '#00008B',
    subPages: ['Foundations', 'Membership', 'Social Services'],
  },
  {
    icon: Truck,
    name: 'Transportation',
    description: 'Passenger experience, logistics feedback, fleet operations.',
    href: '/industries/transportation',
    color: '#3b82f6',
    subPages: ['Airlines & Airports', 'Logistics', 'Public Transit'],
  },
  {
    icon: Laptop,
    name: 'Technology',
    description: 'Product research, user feedback, feature validation.',
    href: '/industries/technology',
    color: '#8b5cf6',
    subPages: ['SaaS', 'Consumer Electronics'],
  },
  {
    icon: Hotel,
    name: 'Hospitality',
    description: 'Guest experience, service quality, loyalty research.',
    href: '/industries/hospitality',
    color: '#00008B',
    subPages: ['Hotels', 'Restaurants'],
  },
  {
    icon: GraduationCap,
    name: 'Education',
    description: 'Student feedback, faculty research, alumni engagement.',
    href: '/industries/education',
    color: '#3b82f6',
    subPages: ['Higher Education', 'K-12', 'Research Institutions'],
  },
  {
    icon: Landmark,
    name: 'Government',
    description: 'Airports, transit, utilities, parks & recreation.',
    href: '/industries/government',
    color: '#8b5cf6',
    subPages: ['Airports', 'Transit', 'Utilities', 'Parks'],
  },
  {
    icon: Briefcase,
    name: 'Professional Services',
    description: 'Agencies, staffing, and consulting firms.',
    href: '/industries/professional-services',
    color: '#00008B',
    subPages: ['Agencies', 'Staffing', 'Consulting'],
  },
]

export default function IndustriesIndex() {
  return (
    <div>
      <Seo
        title="Industries — Feedback Solutions by Sector"
        description="Conversational feedback and surveys for financial services, healthcare, technology, hospitality, education, government, nonprofits, and more."
        path="/industries"
      />

      {/* Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Industries
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
              Built for Your Industry
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto leading-relaxed">
              Pre-built templates and workflows for every vertical. Get started faster with
              industry-specific question sets and benchmarks.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">All verticals</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Explore by Industry</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {industries.map((industry, i) => (
              <FadeIn key={industry.name} delay={i * 0.04}>
                <Link to={industry.href} className="block group">
                  <div className="relative h-full">
                    <div aria-hidden="true" className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" style={{ backgroundColor: industry.color }} />
                    <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 h-full">
                      <industry.icon className="w-7 h-7 text-intelligence-700 mb-3" />
                      <h3 className="font-bold text-intelligence-900 text-xl mb-2">{industry.name}</h3>
                      <p className="text-intelligence-600 text-sm mb-3">{industry.description}</p>
                      {industry.subPages.length > 0 && (
                        <div className="text-xs text-intelligence-500 mb-3">
                          {industry.subPages.join(' · ')}
                        </div>
                      )}
                      <span className="inline-flex items-center text-sm font-bold text-intelligence-900">
                        Explore {industry.name} <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">Your move</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Don&apos;t see your industry?</h2>
            <p className="text-lg text-intelligence-400 mb-10">
              PrismScope works for any organization that needs to understand feedback. Run your first campaign and
              customize for your needs.
            </p>
            <LoopCTAs location="industries-index-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
